import type { UseQueryReturnType } from '@tanstack/vue-query';
import { httpGet } from '@/api/http';
import { V2_CALL_HISTORY } from '@/constants/endpoint';
import type { CallHistoryEntryData } from '@/data/CallHistoryEntry';
import type { AxiosError } from 'axios';
import { useContacts } from '@/api/useContacts';
import { computed, onUnmounted, type Ref, ref } from 'vue';
import { useGetContactByNumber } from '@/api/useGetContactByNumber';
import { useAuthenticatedQuery } from '@/utils/query';
import { useUserStore } from '@/stores/useUserStore';

export const CALL_HISTORY_QUERY_KEY = 'callHistory';

// need to explicitly type due to issue
// https://github.com/TanStack/query/issues/6318
export const useLoggedInUserCallHistory = (
  interval?: Ref<boolean>
): UseQueryReturnType<CallHistoryEntryData[] | undefined, AxiosError<unknown, any>> => {
  const $user = useUserStore();
  const _uuid = computed(() => $user._loggedInUser.uuid);
  return useAuthenticatedQuery({
    queryKey: [CALL_HISTORY_QUERY_KEY, _uuid],
    queryFn: () => httpGet<CallHistoryEntryData>(V2_CALL_HISTORY),
    // TODO switch the refetchInterval to refetch once only when there is a new missed call
    refetchInterval: () => (interval?.value ? 30_000 : false),
    enabled: () => !!_uuid.value,
    gcTime: Infinity,
  });
};

export const useCallHistory = () => {
  const refetchInterval = ref(true);
  const $callHistory = useLoggedInUserCallHistory(refetchInterval);
  const { data: contacts, isLoading: isLoadingContacts } = useContacts();
  const { getContactByPhoneNumber } = useGetContactByNumber(contacts);

  onUnmounted(() => {
    refetchInterval.value = false;
  });

  const _callHistoryData = computed(() => {
    if (!$callHistory.data?.value) return [];
    return $callHistory.data.value.map((call) => {
      const _contact = getContactByPhoneNumber(call.number);
      return { ...call, contact: _contact } as CallHistoryEntryData;
    });
  });

  const isLoading = computed(() => isLoadingContacts.value || $callHistory.isLoading.value);
  return { data: _callHistoryData, isLoading };
};
